/**
 * One child's money, on the child's own terms.
 *
 * The same screen serves the parent and the child, because the alternative —
 * two screens showing the same balance — is two chances for them to disagree,
 * and the whole product rests on the number being the number. What differs is
 * what you can *do*: a parent can correct a mistake and set the bonus rate; a
 * child sorts their own money and reads their own history. Neither view shows
 * another child's numbers.
 *
 * Money arrives unsorted and stays that way until the child splits it. That is
 * not an oversight in the deposit path, it is the lesson (see `project` in
 * domain/ledger.js): an app that quietly files everything into Spend has made
 * the only decision worth making.
 */

import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { BackLink, RowLink, RowList } from '../ui/Nav.jsx';
import { useAuth } from '../lib/auth.jsx';
import { useFamily } from '../lib/family.jsx';
import {
  BUCKET_LABEL, CADENCE_RATE, EMPTY_BALANCE, METHOD_LABEL,
  allocate as allocateMoney, reverse, useBank,
} from '../lib/bank.js';
import { BUCKETS } from '../domain/ledger.js';
import { allowanceAt } from '../domain/allowance.js';
import { allocate as splitCents, centsFromDollars, formatCents } from '../domain/money.js';

const dollars = (cents) => (cents / 100).toFixed(2);

/**
 * A line of history in plain words.
 *
 * The savings bonus is never called interest here. It is a bonus, and the copy
 * says who funds it — the family — because a child reading "interest" learns a
 * word for something a bank does, and this is a promise a parent is keeping.
 */
function historyLabel(entry, familyName) {
  switch (entry.reasonType) {
    case 'deposit': return entry.note || `From ${familyName}`;
    case 'interest':
    case 'match': return `Savings bonus from ${familyName}`;
    case 'allocation': return `Sorted into ${BUCKET_LABEL[entry.bucket] ?? 'a bucket'}`;
    case 'settlement': return `Paid out (${METHOD_LABEL[entry.snapshot?.method] ?? 'cash'})`;
    case 'allowance': return 'Allowance';
    case 'gold_conversion': return 'Gold turned into money';
    case 'opening_balance': return 'What was already saved';
    default: return entry.note || 'Entry';
  }
}

/**
 * Splitting what came in.
 *
 * The button stays disabled until the three amounts add up to exactly what is
 * unsorted, because a partial split leaves money in a state nobody chose and
 * the child would have to come back to a screen they thought they were done
 * with. The preset is a shortcut, not a default: nothing is pre-filled.
 */
function AllocateForm({ unallocatedCents, onSubmit, busy }) {
  const [amounts, setAmounts] = useState({ spend: '', save: '', give: '' });
  const cents = Object.fromEntries(BUCKETS.map((b) => [b, centsFromDollars(amounts[b])]));
  const sum = BUCKETS.reduce((t, b) => t + cents[b], 0);
  const left = unallocatedCents - sum;

  function preset() {
    // Largest-remainder, so the three parts add up to exactly what is there.
    const [spend, save, give] = splitCents(unallocatedCents, [50, 40, 10]);
    setAmounts({ spend: dollars(spend), save: dollars(save), give: dollars(give) });
  }

  return (
    <form
      className="stack tight"
      onSubmit={(e) => { e.preventDefault(); onSubmit(cents); }}
    >
      <div className="row">
        {BUCKETS.map((b) => (
          <label key={b} style={{ flex: '1 1 6rem' }}>
            {BUCKET_LABEL[b]}
            <input
              type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00"
              value={amounts[b]}
              onChange={(e) => setAmounts((a) => ({ ...a, [b]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <p className="small muted" aria-live="polite">
        {left === 0
          ? 'That adds up.'
          : left > 0
            ? `${formatCents(left)} still to place.`
            : `${formatCents(-left)} more than there is.`}
      </p>
      <div className="row">
        <button className="primary" type="submit" disabled={busy || left !== 0 || sum <= 0}>
          {busy ? 'Sorting…' : 'Sort it'}
        </button>
        <button type="button" className="quiet" onClick={preset}>Split it 50/40/10</button>
      </div>
    </form>
  );
}

/** A correction, with the reason the ledger insists on. */
function UndoForm({ entry, familyName, onSubmit, busy }) {
  const [reason, setReason] = useState('');
  return (
    <form
      className="stack tight"
      onSubmit={(e) => { e.preventDefault(); onSubmit(reason.trim()); }}
    >
      <label>
        Why it is being undone
        <input
          value={reason} maxLength={120} required autoFocus placeholder="Typed the wrong amount"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <p className="small muted">
        {historyLabel(entry, familyName)} for {formatCents(entry.amountCents, { sign: true })} stays on
        the list with the correction next to it. Nothing is deleted.
      </p>
      <button className="primary" type="submit" disabled={busy || !reason.trim()}>
        {busy ? 'Undoing…' : 'Undo it'}
      </button>
    </form>
  );
}

export default function ChildBank() {
  const { childId } = useParams();
  const { family, kids, profile } = useFamily();
  const { user } = useAuth();
  const bank = useBank(family?.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [undoing, setUndoing] = useState(false);

  // A child only ever gets their own page, whatever the URL says.
  if (profile && profile.id !== childId) return <Navigate to={`/bank/${profile.id}`} replace />;

  const child = kids.find((k) => k.id === childId);
  if (!bank.loading && !child) return <Navigate to={profile ? '/' : '/bank'} replace />;

  const balance = bank.byChild[childId]?.balance ?? EMPTY_BALANCE;
  const entries = bank.byChild[childId]?.entries ?? [];

  /*
   * The one number a child is allowed to state without asking anybody.
   *
   * `baseCents` and not the target: the target includes the part that has to be
   * earned, and quoting a child a figure the ledger will not pay them is the
   * small betrayal the gold-flooring rule exists to prevent. Priced from the
   * birth month and year, never from a stored age.
   */
  const allowance = bank.allowanceRules[childId] ?? null;
  const allowanceCents = allowance && child
    ? allowanceAt(bank.today, { birthMonth: child.birth_month, birthYear: child.birth_year }, allowance).baseCents
    : 0;

  const reversed = new Set(entries.filter((e) => e.reversesId).map((e) => e.reversesId));
  // Reversal rows are never their own line: the strikethrough on the entry they
  // correct is the display of them, and showing both says the same thing twice.
  const shown = entries.filter((e) => e.reasonType !== 'reversal').slice().reverse();
  // The most recent thing a parent could plausibly have got wrong. Older
  // corrections are a different act and belong to a screen with more room for
  // them than a button next to a balance.
  const undoable = !profile
    ? shown.find((e) => ['deposit', 'settlement'].includes(e.reasonType) && !reversed.has(e.id))
    : null;

  async function sort(cents) {
    setBusy(true); setError(null);
    try {
      await allocateMoney({
        familyId: family.id,
        childId,
        cents: balance.unallocated,
        weights: cents,
        actorId: user.id,
        occurredAt: bank.today,
      });
      await bank.reload();
    } catch (e) {
      setError(`That didn’t save — ${e.message}.`);
    }
    setBusy(false);
  }

  async function undo(reason) {
    setBusy(true); setError(null);
    try {
      await reverse({
        familyId: family.id, entry: undoable, reason, actorId: user.id, occurredAt: bank.today,
      });
      setUndoing(false);
      await bank.reload();
    } catch (e) {
      setError(`That didn’t save — ${e.message}.`);
    }
    setBusy(false);
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        {/* A child is on their own tab, so the top-left says whose money it
            is; a parent came from the bank, so it says the way back. */}
        {profile
          ? <p className="eyebrow">{family.name}</p>
          : <BackLink to="/bank">Bank</BackLink>}
        <h1>{profile ? 'Your money' : (child?.display_name ?? 'The bank')}</h1>
      </div>

      {bank.error && <p className="notice error">{bank.error}</p>}
      {error && <p className="notice error" role="alert">{error}</p>}
      {bank.loading && <p className="muted" role="status">Loading…</p>}

      <div className="card stack tight">
        <p className="eyebrow">Altogether</p>
        <p className="money" style={{ fontSize: 'var(--size-2xl)' }}>{formatCents(balance.total)}</p>
      </div>

      <div className="row" style={{ alignItems: 'stretch' }}>
        {BUCKETS.map((b) => (
          <div className="card stack tight" key={b} style={{ flex: '1 1 7rem' }}>
            <p className="eyebrow">{BUCKET_LABEL[b]}</p>
            <p className="money">{formatCents(balance[b])}</p>
          </div>
        ))}
      </div>

      {/*
        Quiet on purpose, and only on the child's own view: a parent has the
        rule screen a tap away, where the number comes with what it costs them.
      */}
      {profile && allowance && allowanceCents > 0 && (
        <p className="small muted">
          Allowance: <span className="money">{formatCents(allowanceCents)}</span>{' '}
          {CADENCE_RATE[allowance.cadence] ?? CADENCE_RATE.weekly}.
        </p>
      )}

      {balance.unallocated > 0 && (
        <div className="card stack tight">
          <div className="spread">
            <h2>Unsorted</h2>
            <span className="money">{formatCents(balance.unallocated)}</span>
          </div>
          <p className="small muted">
            {profile
              ? 'Yours to split. Put it wherever you want it — you can change what you do next time.'
              : `${child?.display_name ?? 'They'} decides where this goes.`}
          </p>
          <AllocateForm unallocatedCents={balance.unallocated} onSubmit={sort} busy={busy} />
        </div>
      )}

      {undoable && (
        <div className="card stack tight">
          <div className="spread">
            <span className="small muted">Last entry: {historyLabel(undoable, family.name)}</span>
            <button className="quiet" onClick={() => setUndoing((u) => !u)}>
              {undoing ? 'Keep it' : 'Undo'}
            </button>
          </div>
          {undoing && (
            <UndoForm entry={undoable} familyName={family.name} onSubmit={undo} busy={busy} />
          )}
        </div>
      )}

      {shown.length > 0 && (
        <section className="card stack tight" aria-labelledby="bank-history">
          <p className="eyebrow" id="bank-history">History</p>
          <ul className="stack tight" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {shown.map((e) => {
              const undone = reversed.has(e.id);
              return (
                <li key={e.id} className="spread small">
                  <span
                    className={undone ? 'faint' : ''}
                    style={undone ? { textDecoration: 'line-through' } : undefined}
                  >
                    {historyLabel(e, family.name)}
                    {undone && <span className="faint"> · undone</span>}
                  </span>
                  <span className={`money ${undone ? 'faint' : ''}`}>
                    {formatCents(e.amountCents, { sign: true })}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="small faint">Corrections stay on the list.</p>
        </section>
      )}

      {!bank.loading && shown.length === 0 && (
        <p className="muted">Nothing has happened here yet.</p>
      )}

      {/* The other half of what this money is for: the list of things it is
          being kept for. One link, on both views — a wishlist a child cannot
          reach from their own balance is a wishlist nobody adds to. */}
      <RowList>
        <RowLink
          to={`/wishes/${childId}`}
          title={profile ? 'Your list' : 'Their list'}
          detail="What this money is being kept for"
        />
        {!profile && (
          <RowLink
            to={`/bank/${childId}/allowance`}
            title="Allowance"
            detail={allowance && allowanceCents > 0
              ? `${formatCents(allowanceCents)} ${CADENCE_RATE[allowance.cadence] ?? CADENCE_RATE.weekly}`
              : 'Not set'}
          />
        )}
        {!profile && (
          <RowLink to={`/bank/${childId}/savings`} title="Savings bonus" />
        )}
      </RowList>
    </main>
  );
}
